<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';
import { t } from '../i18n';
import MiniDropdown from './MiniDropdown.vue';

const props = defineProps<{ modelValue: string; error?: string }>();
const emit = defineEmits<{ 'update:modelValue': [value: string] }>();
const labelId = useId();
const errorId = useId();

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 101 }, (_, index) => currentYear - index);
const monthKeys = [
    'Січень',
    'Лютий',
    'Березень',
    'Квітень',
    'Травень',
    'Червень',
    'Липень',
    'Серпень',
    'Вересень',
    'Жовтень',
    'Листопад',
    'Грудень',
];

const day = ref('');
const month = ref('');
const year = ref('');
const days = computed(() => {
    const selectedYear = Number(year.value || currentYear);
    const selectedMonth = Number(month.value || 1);

    return Array.from(
        { length: new Date(selectedYear, selectedMonth, 0).getDate() },
        (_, index) => index + 1,
    );
});
const dayOptions = computed(() =>
    days.value.map((value) => ({
        value: String(value).padStart(2, '0'),
        label: String(value),
    })),
);
const monthOptions = computed(() =>
    monthKeys.map((label, index) => ({
        value: String(index + 1).padStart(2, '0'),
        label: t(label),
    })),
);
const yearOptions = years.map((value) => ({
    value: String(value),
    label: String(value),
}));

watch(
    () => props.modelValue,
    (value) => {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

        if (match) {
            [, year.value, month.value, day.value] = match;
        } else if (!value) {
            day.value = '';
            month.value = '';
            year.value = '';
        }
    },
    { immediate: true },
);

function update(part: 'day' | 'month' | 'year', value: string): void {
    const fields = { day, month, year };
    fields[part].value = value;

    if (!day.value || !month.value || !year.value) {
        return;
    }

    const maxDay = new Date(
        Number(year.value),
        Number(month.value),
        0,
    ).getDate();
    const safeDay = Math.min(Number(day.value), maxDay);
    const normalizedDay = String(safeDay).padStart(2, '0');
    day.value = normalizedDay;
    emit(
        'update:modelValue',
        `${year.value}-${month.value.padStart(2, '0')}-${normalizedDay}`,
    );
}
</script>

<template>
    <div class="birthday-field">
        <div
            class="birthday-picker"
            :class="{
                'birthday-picker--filled': modelValue,
                'birthday-picker--invalid': error,
            }"
            role="group"
            :aria-labelledby="labelId"
            :aria-invalid="Boolean(error)"
            :aria-describedby="error ? errorId : undefined"
        >
            <span :id="labelId" class="birthday-picker__label">
                {{ t('Дата народження')
                }}<em class="birthday-picker__required" aria-hidden="true"
                    >*</em
                >
            </span>
            <MiniDropdown
                :model-value="day"
                :options="dayOptions"
                :placeholder="t('День')"
                :accessible-label="t('День народження')"
                :invalid="Boolean(error)"
                @update:model-value="update('day', $event)"
            />
            <MiniDropdown
                :model-value="month"
                :options="monthOptions"
                :placeholder="t('Місяць')"
                :accessible-label="t('Місяць народження')"
                :invalid="Boolean(error)"
                @update:model-value="update('month', $event)"
            />
            <MiniDropdown
                :model-value="year"
                :options="yearOptions"
                :placeholder="t('Рік')"
                :accessible-label="t('Рік народження')"
                :invalid="Boolean(error)"
                @update:model-value="update('year', $event)"
            />
        </div>
        <p v-if="error" :id="errorId" class="birthday-field__error">
            {{ error }}
        </p>
    </div>
</template>

<style scoped>
.birthday-picker {
    position: relative;
    display: grid;
    grid-template-columns: 0.8fr 1.35fr 0.9fr;
    height: 58px;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 1px solid color-mix(in srgb, var(--hint) 30%, transparent);
    border-radius: 16px;
    background: var(--surface);
}
.birthday-picker:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 12%, transparent);
}
.birthday-picker--invalid,
.birthday-picker--invalid:focus-within {
    border-color: color-mix(in srgb, var(--danger) 72%, transparent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--danger) 10%, transparent);
}
.birthday-picker__required {
    margin-left: 3px;
    color: var(--danger);
    font-style: normal;
}
.birthday-picker__label {
    position: absolute;
    z-index: 2;
    top: 0;
    left: 16px;
    transform: translateY(-50%);
    padding: 0 7px;
    /* The label straddles the top border to cut it, so what is painted behind it has to
       match BOTH sides: the page above the border, the field below it. A flat `var(--bg)`
       matched the page and was too dark for the field — a black slab across the top of the
       control. `translateY(-50%)` puts the border exactly halfway down the label, so the
       split sits at 50%.

       A real <legend> would cut the border with nothing painted at all, and was tried: in a
       fieldset the legend takes part in the flow, the three dropdowns are pushed down, and
       their dividers run out through the bottom of the box. Not worth a rebuilt control. */
    background: linear-gradient(
        to bottom,
        var(--bg) 0,
        var(--bg) 50%,
        var(--surface) 50%,
        var(--surface) 100%
    );
    color: var(--hint);
    font: 500 12px/1 var(--app-font);
    pointer-events: none;
}
.birthday-picker--filled .birthday-picker__label,
.birthday-picker:focus-within .birthday-picker__label {
    color: var(--accent);
}
.birthday-picker--invalid .birthday-picker__label,
.birthday-picker--invalid:focus-within .birthday-picker__label {
    color: var(--danger);
}
.birthday-picker :deep(.mini-dropdown__trigger) {
    height: 56px;
}
.birthday-picker :deep(.mini-dropdown + .mini-dropdown) {
    border-left: 1px solid color-mix(in srgb, var(--hint) 20%, transparent);
}
.birthday-picker:has(> .mini-dropdown:first-of-type.mini-dropdown--open) {
    border-bottom-left-radius: 0;
}
.birthday-picker:has(> .mini-dropdown:last-of-type.mini-dropdown--open) {
    border-bottom-right-radius: 0;
}
.birthday-field__error {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 6px 4px 0;
    color: var(--danger);
    font-size: 11px;
    line-height: 1.35;
}
.birthday-field__error::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
}
@media (max-width: 370px) {
    .birthday-picker :deep(.mini-dropdown__trigger) {
        padding-right: 8px;
        padding-left: 9px;
        font-size: 12px;
    }
}
</style>
